import {
  CheckOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  InfoCircleFilled,
  PlusOutlined,
  ProjectOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { ConfigProvider, Divider, Input, message, Modal } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  createView,
  deleteView,
  setDefaultView,
  updateWorkspaceView,
  updateWorkspaceViewsOrder,
} from '../../api/workspace';
import { ArchiveBox } from '../../assets/CustomIcons/ArchiveBox';
import { FlagDefault } from '../../assets/CustomIcons/FlagDefault';
import { SortAscending } from '../../assets/CustomIcons/SortAscending';
import { SortDescending } from '../../assets/CustomIcons/SortDescending';
import { SprintIcon } from '../../assets/CustomIcons/SprintIcon';
import { TriageTriangle } from '../../assets/CustomIcons/TriageTriangle';
import colorTokens from '../../constants/design_system/colors';
import { queryClient } from '../../queryClient';
import { useCurrentWorkspaceEpics } from '../../serverState/epics';
import { useGetWorkspace } from '../../serverState/workspace';
import { useFilterStore } from '../../stores/filterStore';
import { defaultTaskStatuses } from '../../types/TaskStatus';
import Workspace, { WorkspaceFramework } from '../../types/Workspace';
import {
  WorkspaceView,
  WorkspaceViewEntityType,
  WorkspaceViewGroup,
  WorkspaceViewGroupUtils,
  WorkspaceViewRepresentation,
} from '../../types/WorkspaceView';
import { Column, ViewColumn, ViewColumnUtils, WorkspaceViewColumn } from '../../types/WorkspaceViewColumn';
import { WorkspaceViewSortDirection } from '../../types/WorkspaceViewSortBy';
import { applyViewsOrder, unequalViewIndexes } from '../TaskManagement/helpers/viewHelpers';
import { BaseButton } from '../UI/BaseButton';
import { CustomSelect } from '../UI/CustomSelect';
import { ViewColumnComponentType, ViewColumnsGroup } from './components/ViewColumnsGroup';
import ViewRepresentationCard from './components/ViewRepresentationCard';
import { ViewsGroup } from './components/ViewsGroup';
import { BacklogCircle } from '../../assets/CustomIcons/BacklogCircle';
import { EpicIcon } from '../../assets/CustomIcons/EpicIcon';
import { ConfirmSaveForEveryoneModal } from './ConfirmSaveForEveryoneModal';

interface ManageViewsModalProps {
  open: boolean;
  onOk: () => void;
  onCancel: () => void;
  view: WorkspaceView | null;
  setView: (view: WorkspaceView) => void;
  views: WorkspaceView[];
  setViews: (views: WorkspaceView[]) => void;
}

export const ManageViewsModal = (props: ManageViewsModalProps) => {
  const { open, onCancel, onOk, view, setView, views, setViews } = props;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workspaceQuery = useGetWorkspace();
  const epicsQuery = useCurrentWorkspaceEpics();
  const framework = workspaceQuery.data?.framework || WorkspaceFramework.none;
  const viewGroups = useMemo(() => getViewGroups(framework), [framework]);

  // Zustand filter store
  const { resetFilters } = useFilterStore();

  const [selectedView, setSelectedView] = useState<WorkspaceView>(view ?? getDefaultView());
  const [pendingSelectedViewId, setPendingSelectedViewId] = useState<number | null>(null);
  const [columns, setColumns] = useState<ViewColumn[]>(ViewColumnUtils.updateAndSortColumn(framework, selectedView));
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedViewName, setSelectedViewName] = useState<string>(selectedView.name ?? 'View');
  const [groupBy, setGroupBy] = useState<string>(Column.epic);
  const [sortBy, setSortBy] = useState<string>('Manual');
  const [sortDirection, setSortDirection] = useState<WorkspaceViewSortDirection>(WorkspaceViewSortDirection.desc);
  const [isConfirmSaveModalOpen, setIsConfirmSaveModalOpen] = useState(false);

  const unequalViewIds = useMemo(
    () => unequalViewIndexes(framework, views, workspaceQuery.data?.Views || []),
    [views, workspaceQuery.data?.Views]
  );

  const viewNameInputRef = useRef(null);

  useEffect(() => {
    const selView = views.find((view) => view.id === selectedView.id);
    if (selView) {
      setSelectedView(selView);
    }
  }, [views]);

  // Handle pending view selection after views update
  useEffect(() => {
    if (pendingSelectedViewId) {
      const pendingView = views.find((view) => view.id === pendingSelectedViewId);
      if (pendingView) {
        setSelectedView(pendingView);
        setSelectedViewName(pendingView.name);
        setColumns(ViewColumnUtils.updateAndSortColumn(framework, pendingView));
        const pathname = location.pathname.split('/').slice(0, -1).join('/').concat(`/${pendingView.group}`);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('view', pendingView.publicId.toString());
        navigate(`${pathname}?${newSearchParams.toString()}`);
        setPendingSelectedViewId(null);
      }
    }
  }, [views, pendingSelectedViewId]);

  useEffect(() => {
    const selView = view ?? getDefaultView();
    setSelectedView(selView);
    setColumns(ViewColumnUtils.updateAndSortColumn(framework, selView));
  }, [view]);

  useEffect(() => {
    setSelectedViewName(selectedView.name);
  }, [selectedView.name]);

  useEffect(() => {
    setGroupBy(selectedView.groupBy || Column.epic);
    setSortBy(selectedView.sortBy?.column || 'Manual');
    setSortDirection(selectedView.sortBy?.direction ?? WorkspaceViewSortDirection.desc);
  }, [selectedView]);

  function getDefaultView() {
    const viewId = searchParams.get('view');
    return views.find((view) => view.id.toString() === viewId) || views[0];
  }

  const updateName = (e: { target: { value: string } }) => {
    const newValue: string = e.target.value;
    const trimmedNewValue = newValue.trim();
    if (!trimmedNewValue) {
      return;
    }
    if (trimmedNewValue === selectedView.name) {
      return;
    }

    updateViewNameForAll(trimmedNewValue);
  };

  const updateViews = async (groupViews: WorkspaceView[], group: WorkspaceViewGroup) => {
    // Create a lookup map for the updated views
    const updatedViewsById = Object.fromEntries(groupViews.map((view) => [view.id, view]));
    // Update only the views that changed, keep everything else as-is
    const updatedViews = views.map((view) => updatedViewsById[view.id] || view);
    setViews(updatedViews);
    if (workspaceQuery.data) {
      const viewsOrderMapping: Record<number, number> = groupViews.reduce(
        (acc, status) => {
          acc[status.id] = status.orderIndex ?? 0;
          return acc;
        },
        {} as Record<number, number>
      );
      queryClient.setQueryData(['workspace', Number(workspaceQuery.data.id)], (oldData: Workspace) => {
        return {
          ...oldData,
          Views: applyViewsOrder(oldData.Views, viewsOrderMapping),
        };
      });

      await updateWorkspaceViewsOrder(Number(workspaceQuery.data.id), viewsOrderMapping);
    }
  };

  const updateSelectedView = (view: WorkspaceView) => {
    setSelectedView(view);
    setSelectedViewName(view.name);
    setColumns(ViewColumnUtils.updateAndSortColumn(framework, view));
    const pathname = location.pathname.split('/').slice(0, -1).join('/').concat(`/${view.group}`);
    searchParams.set('view', view.publicId.toString());
    navigate(`${pathname}?${searchParams.toString()}`);
  };

  const updateColumns = (columns: ViewColumn[]) => {
    setColumns(columns);
    updateViewLocally(columns);
  };

  const updateColumnsSelection = (columnId: string, isSelected: boolean) => {
    const newColumns = columns.map((column) => (column.id === columnId ? { ...column, selected: isSelected } : column));
    setColumns(newColumns);
    updateViewLocally(newColumns);
  };

  const updateGroupBy = (groupBy: string) => {
    setGroupBy(groupBy);
    updateViewLocally(undefined, undefined, undefined, groupBy);
  };

  const updateSortBy = (sortBy: string) => {
    setSortBy(sortBy);
    updateViewLocally(undefined, undefined, undefined, undefined, sortBy);
  };

  const updateSortDirection = () => {
    const direction =
      sortDirection === WorkspaceViewSortDirection.asc
        ? WorkspaceViewSortDirection.desc
        : WorkspaceViewSortDirection.asc;
    setSortDirection(direction);
    updateViewLocally(undefined, undefined, undefined, undefined, undefined, direction);
  };

  const updateViewLocally = (
    columns?: ViewColumn[],
    name?: string,
    representation?: WorkspaceViewRepresentation,
    groupBy?: string,
    sortBy?: string,
    sortDirection?: WorkspaceViewSortDirection,
    entityType?: WorkspaceViewEntityType
  ) => {
    const serverColumns: WorkspaceViewColumn | undefined = columns
      ? {
          hidden: columns.filter((column) => !column.selected).map((column) => column.id),
          order: columns.map((column) => column.id),
        }
      : selectedView.columns;
    const sortByColumn = sortBy ?? selectedView.sortBy?.column;
    const sortByDirection = sortDirection ?? selectedView.sortBy?.direction;
    const sortByObj = sortByColumn || sortByDirection ? { column: sortByColumn, direction: sortByDirection } : null;
    const updatedView = {
      ...selectedView,
      name: name ?? selectedView.name,
      columns: serverColumns,
      representation: representation ?? selectedView.representation,
      entityType: entityType ?? selectedView.entityType,
      groupBy: groupBy ?? selectedView.groupBy,
      sortBy: sortByObj ?? selectedView.sortBy,
    };

    const updatedViews = views.map((view) => {
      if (view.id === updatedView.id) {
        return updatedView;
      }
      return view;
    });
    setViews(updatedViews);
    setSelectedView(updatedView);

    if (searchParams.get('view') === updatedView.publicId.toString()) {
      setView(updatedView);
    }
  };

  const resetToSavedViews = () => {
    const savedViews = workspaceQuery.data?.Views ?? views;
    const savedSelectedView = savedViews.find((view) => view.id === selectedView.id) || getDefaultView();

    // Reset views state
    setViews(savedViews);
    setView(savedSelectedView);
    setSelectedView(savedSelectedView);
    setColumns(ViewColumnUtils.updateAndSortColumn(framework, savedSelectedView));

    // Reset filters using Zustand store
    if (epicsQuery.data && workspaceQuery.data) {
      const usersWithAccess = workspaceQuery.data.UsersWithAccess?.map((access) => access.User) || [];
      const statusOptions = (workspaceQuery.data.TaskStatus as any[]) || defaultTaskStatuses;
      const labels = workspaceQuery.data.Labels || [];

      resetFilters(
        savedSelectedView.workspaceId,
        savedSelectedView.id,
        epicsQuery.data,
        usersWithAccess,
        statusOptions,
        labels,
        workspaceQuery.data.estimationType,
        workspaceQuery.data.framework || WorkspaceFramework.none,
        savedSelectedView.group || WorkspaceViewGroup.backlog,
        savedSelectedView.entityType || WorkspaceViewEntityType.task,
        Array.isArray(savedSelectedView.filters) ? savedSelectedView.filters : undefined
      );
    }
  };

  const saveViewsForAll = async () => {
    const viewsToSave = views.filter((view) => unequalViewIds.includes(view.id));
    const promises = viewsToSave.map(async (view) => {
      await updateWorkspaceView(view.workspaceId, view.id, view);
    });
    await Promise.all(promises);
    void messageApi.success('Views saved successfully');

    queryClient.setQueryData(['workspace', selectedView.workspaceId], (oldData: Workspace) => {
      return {
        ...oldData,
        Views: views,
      };
    });
  };

  const handleSaveForAllClick = () => {
    setIsConfirmSaveModalOpen(true);
  };

  const handleConfirmSave = () => {
    setIsConfirmSaveModalOpen(false);
    void saveViewsForAll();
  };

  const handleCancelConfirm = () => {
    setIsConfirmSaveModalOpen(false);
  };

  const updateViewNameForAll = async (name: string) => {
    const updatedView = {
      ...selectedView,
      name: name,
    };

    const updatedViews = views.map((view) => {
      if (view.id === updatedView.id) {
        return updatedView;
      }
      return view;
    });

    queryClient.setQueryData(['workspace', Number(updatedView.workspaceId)], (oldData: Workspace) => {
      return {
        ...oldData,
        Views: updatedViews,
      };
    });
    setViews(updatedViews);
    if (searchParams.get('view') === updatedView.publicId.toString()) {
      setView(updatedView);
    }
    setSelectedView(updatedView);

    await updateWorkspaceView(updatedView.workspaceId, updatedView.id, {
      name: name,
    });
  };

  const onSetDefault = async () => {
    if (selectedView.default) {
      return;
    }

    const updatedViews = views.map((view) => {
      if (view.group === selectedView.group) {
        return { ...view, default: view.id === selectedView.id };
      }
      return view;
    });

    queryClient.setQueryData(['workspace', Number(selectedView.workspaceId)], (oldData: Workspace) => {
      return {
        ...oldData,
        Views: updatedViews,
      };
    });
    setViews(updatedViews);
    setSelectedView({ ...selectedView, default: true });

    await setDefaultView(selectedView.workspaceId, selectedView.id);

    void messageApi.success(`View ${selectedView.name} has been set as default.`);
  };

  const showDeleteModal = async () => {
    if (views.filter((view) => view.group === selectedView.group).length === 1) {
      void messageApi.error('Can not delete last view in the group.');
      return;
    }
    setIsDeleteModalVisible(true);
  };

  const deleteSelectedView = async () => {
    if (selectedView.default) {
      return;
    }
    try {
      await deleteView(selectedView.workspaceId, selectedView.id);
    } catch (error) {
      void messageApi.error(error.response.data.message);
      return;
    }

    const updatedViews = views.filter((view) => view.id !== selectedView.id);

    // Find the default view in the same group, or first view in the group
    const fallbackView =
      updatedViews.find((view) => view.group === selectedView.group && view.default) ||
      updatedViews.find((view) => view.group === selectedView.group) ||
      updatedViews[0];

    queryClient.setQueryData(['workspace', Number(selectedView.workspaceId)], (oldData: Workspace) => {
      return {
        ...oldData,
        Views: updatedViews,
      };
    });
    setViews(updatedViews);

    if (fallbackView) {
      setPendingSelectedViewId(fallbackView.id);
    }

    setIsDeleteModalVisible(false);
  };

  const createNewView = async (group: WorkspaceViewGroup) => {
    // Generate name using current views state to ensure uniqueness
    const defaultName = generateDefaultName();

    const isEpicsGroup = group === WorkspaceViewGroup.epics;
    const entityType = isEpicsGroup ? WorkspaceViewEntityType.epic : WorkspaceViewEntityType.task;
    const serverColumns: WorkspaceViewColumn = {
      hidden: [],
      order: ViewColumnUtils.defaultColumns(framework, { group, entityType }).map((column) => column.id),
    };
    const newViewResponse = await createView(
      selectedView.workspaceId,
      defaultName,
      group,
      WorkspaceViewRepresentation.table,
      entityType,
      Column.epic,
      serverColumns,
      { column: 'Manual', direction: WorkspaceViewSortDirection.desc }
    );
    const newView = newViewResponse.data;

    // Ensure the new view has a proper orderIndex
    const maxOrderIndex = views
      .filter((v) => v.group === group)
      .reduce((max, view) => Math.max(max, view.orderIndex ?? view.id), 0);

    const newViewWithOrder = {
      ...newView,
      orderIndex: maxOrderIndex + 1,
    };

    const updatedViews = [...views, newViewWithOrder];

    queryClient.setQueryData(['workspace', Number(selectedView.workspaceId)], (oldData: Workspace) => {
      return {
        ...oldData,
        Views: updatedViews,
      };
    });
    setViews(updatedViews);
    setPendingSelectedViewId(newViewWithOrder.id);

    setTimeout(() => {
      viewNameInputRef?.current?.focus();
      viewNameInputRef?.current?.select();
    }, 400);
  };

  const generateDefaultName = () => {
    const baseName = 'View';
    let newName = baseName;
    let counter = 1;

    const titles = views.map((view) => view.name);

    while (titles.includes(newName)) {
      counter += 1;
      newName = `${baseName} ${counter}`;
    }

    return newName;
  };

  return (
    <>
      {contextHolder}
      <ConfirmSaveForEveryoneModal
        isOpen={isConfirmSaveModalOpen}
        onConfirm={handleConfirmSave}
        onCancel={handleCancelConfirm}
        mode="view"
      />
      <Modal
        open={open}
        title={<div className="text-brand-grey-dark text-base font-semibold leading-normal ml-5">Manage Views</div>}
        onCancel={onCancel}
        onOk={() => onOk()}
        footer={null}
        width={920}
        centered
      >
        <div className="h-[480px] flex mt-5 mb-2">
          <div className="w-[252px] flex flex-col gap-2 overflow-y-auto px-5 mt-2">
            {viewGroups.map((group) => {
              return (
                <ViewGroupComponent
                  key={group}
                  views={views.filter((view) => view.group === group)}
                  selectedView={selectedView}
                  setSelectedView={updateSelectedView}
                  updateViews={updateViews}
                  createView={() => createNewView(group)}
                  changedViews={unequalViewIds}
                  group={group}
                />
              );
            })}
          </div>
          <div className="w-px min-h-full bg-neutral-300 rounded-sm" />
          <div className="mx-4 flex flex-col flex-1">
            <>
              <div className="flex justify-between ml-2 flex-1 min-h-0">
                <div className="w-[232px] flex flex-col gap-2 overflow-y-auto min-h-0">
                  <div className="text-neutral-900 text-xs font-semibold leading-none">View Name</div>
                  <div className="flex items-center gap-2 mt-2">
                    {selectedView.representation === WorkspaceViewRepresentation.table && (
                      <TableOutlined className="text-neutral-900" />
                    )}
                    {selectedView.representation === WorkspaceViewRepresentation.kanban && (
                      <ProjectOutlined className="text-neutral-900" />
                    )}
                    <Input
                      className="h-7 text-brand-grey-dark text-sm font-normal box-border"
                      size={'small'}
                      ref={viewNameInputRef}
                      value={selectedViewName}
                      onChange={(e) => setSelectedViewName(e.target.value)}
                      onPressEnter={updateName}
                      onBlur={updateName}
                    />
                  </div>

                  <div className="text-neutral-900 text-xs font-semibold mt-2">Layout</div>

                  <div className="h-16 p-1 bg-neutral-300 rounded justify-center items-start inline-flex">
                    <ViewRepresentationCard
                      representation={WorkspaceViewRepresentation.table}
                      setRepresentation={() =>
                        updateViewLocally(undefined, undefined, WorkspaceViewRepresentation.table)
                      }
                      selected={selectedView.representation === WorkspaceViewRepresentation.table}
                      viewGroup={selectedView.group}
                      entityType={selectedView.entityType}
                    />

                    <ViewRepresentationCard
                      representation={WorkspaceViewRepresentation.kanban}
                      setRepresentation={() =>
                        updateViewLocally(undefined, undefined, WorkspaceViewRepresentation.kanban)
                      }
                      selected={selectedView.representation === WorkspaceViewRepresentation.kanban}
                      viewGroup={selectedView.group}
                      entityType={selectedView.entityType}
                    />
                  </div>

                  {selectedView.entityType !== WorkspaceViewEntityType.epic && (
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-neutral-900 text-xs font-semibold">Group by:</div>
                      <CustomSelect
                        options={ViewColumnUtils.groupByColumns(framework, selectedView).map((c) => c.id)}
                        selectedOption={groupBy}
                        selectOption={updateGroupBy}
                        getTitle={(option) => ViewColumnUtils.getTitle(option as Column, framework)}
                        width="150px"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-2">
                    <div className="text-neutral-900 text-xs font-semibold">Sort by:</div>
                    <div className="w-[150px] flex items-center">
                      <CustomSelect
                        options={['Manual', ...ViewColumnUtils.sortByColumns(framework, selectedView).map((c) => c.id)]}
                        selectedOption={sortBy}
                        selectOption={updateSortBy}
                        getTitle={(option) =>
                          option === 'Manual' ? 'Manual' : ViewColumnUtils.getTitle(option as Column, framework)
                        }
                        className="flex-1"
                      />
                      {sortBy !== 'Manual' && (
                        <div
                          className={`h-[23px] w-[23px] flex items-center justify-center rounded border border-solid border-neutral-500 text-neutral-900 hover:border-brand-taskonio-green cursor-pointer ml-1`}
                          onClick={updateSortDirection}
                        >
                          {sortDirection === WorkspaceViewSortDirection.asc ? <SortAscending /> : <SortDescending />}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="w-[299px] flex flex-col overflow-y-auto min-h-0">
                  <div className="text-neutral-900 text-xs font-semibold leading-none mb-2">Properties</div>
                  <ConfigProvider
                    theme={{
                      token: {
                        colorPrimary: colorTokens.blue[600],
                      },
                    }}
                  >
                    <ViewColumnsGroup
                      componentType={ViewColumnComponentType.newSpaceViewModal}
                      columns={columns}
                      updateColumns={(newColumns) => updateColumns(newColumns)}
                      updateColumnSelection={updateColumnsSelection}
                      representation={selectedView.representation}
                    />
                  </ConfigProvider>
                  <div className="pl-2 py-2 bg-neutral-200 rounded justify-start items-center inline-flex gap-2 mt-2">
                    <InfoCircleFilled className="text-neutral-800" />
                    <div className="text-neutral-900 text-xs font-normal leading-none">
                      Values of your hidden properties are saved.
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-auto">
                <Divider />

                <div className="flex justify-between">
                  <BaseButton
                    type="secondary"
                    size="medium"
                    disabled={selectedView.default}
                    danger={true}
                    disabledTooltip="You can not delete a default view"
                    onClick={showDeleteModal}
                  >
                    <DeleteOutlined />
                    Delete
                  </BaseButton>

                  <div className="flex gap-2">
                    {unequalViewIds.length > 0 && unequalViewIds.includes(selectedView.id) && (
                      <div className="flex gap-2">
                        <BaseButton size="medium" type="text" onClick={resetToSavedViews}>
                          Reset
                        </BaseButton>

                        <BaseButton size="medium" type="orange" onClick={handleSaveForAllClick}>
                          <CheckOutlined />
                          Save for Everyone
                        </BaseButton>
                      </div>
                    )}

                    <BaseButton
                      type="secondary"
                      size="medium"
                      disabled={selectedView.default}
                      disabledTooltip="View is already a default"
                      onClick={onSetDefault}
                    >
                      <FlagDefault />
                      Set as Default
                    </BaseButton>
                  </div>
                </div>
              </div>
            </>
          </div>
        </div>

        <Modal
          title={<div className="text-brand-grey-dark text-base font-semibold">{`Delete ${selectedView.name}?`}</div>}
          open={isDeleteModalVisible}
          onCancel={() => setIsDeleteModalVisible(false)}
          zIndex={2000}
          width={640}
          footer={
            <div className="w-full flex justify-end gap-2">
              <BaseButton type="secondary" size="medium" onClick={() => setIsDeleteModalVisible(false)}>
                Cancel
              </BaseButton>
              <BaseButton type="secondary" size="medium" danger onClick={deleteSelectedView}>
                Yes, delete view
              </BaseButton>
            </div>
          }
          centered
        >
          <div className="text-brand-grey-dark text-sm font-normal">
            Deleting views is an irreversible action. Are you sure you want do proceed?{' '}
          </div>
        </Modal>
      </Modal>
    </>
  );
};

const ViewGroupComponent: React.FC<{
  views: WorkspaceView[];
  selectedView: WorkspaceView;
  setSelectedView: (view: WorkspaceView) => void;
  updateViews: (groupViews: WorkspaceView[], group: WorkspaceViewGroup) => void;
  createView: () => void;
  changedViews: number[];
  group: WorkspaceViewGroup;
}> = ({ views, selectedView, setSelectedView, updateViews, createView, changedViews, group }) => {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-brand-grey-dark text-sm font-medium mb-1">
        {getViewGroupIcon(group)}
        {WorkspaceViewGroupUtils.getTitle(group)}
      </div>
      <ViewsGroup
        views={views}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        updateViews={(views) => updateViews(views, group)}
        changedViews={changedViews}
      />
      <div
        className="flex items-center gap-2 px-1.5 py-1.5 cursor-pointer hover:bg-neutral-300 rounded my-1"
        onClick={createView}
      >
        <PlusOutlined className="text-neutral-900" />
        <div className="font-normal text-sm text-neutral-900">New View</div>
      </div>
    </div>
  );
};

const isTriageArchiveEnabled = import.meta.env.VITE_FEATURE_TRIAGE_ARCHIVE === 'true';

const getViewGroups = (framework: WorkspaceFramework): WorkspaceViewGroup[] => {
  let main: WorkspaceViewGroup[];
  if (framework === WorkspaceFramework.scrum) {
    main = [
      WorkspaceViewGroup.epics,
      WorkspaceViewGroup.backlog,
      WorkspaceViewGroup.planning,
      WorkspaceViewGroup.sprint,
    ];
  } else if (framework === WorkspaceFramework.kanban) {
    main = [WorkspaceViewGroup.epics, WorkspaceViewGroup.backlog, WorkspaceViewGroup.kanban];
  } else {
    main = [WorkspaceViewGroup.epics, WorkspaceViewGroup.backlog];
  }
  if (!isTriageArchiveEnabled) return main;
  // Epics must stay first, with Triage right after it: Epics, Triage, ...rest, Archive.
  const [epics, ...rest] = main;
  return [epics, WorkspaceViewGroup.triage, ...rest, WorkspaceViewGroup.archive];
};

const getViewGroupIcon = (item: WorkspaceViewGroup) => {
  switch (item) {
    case WorkspaceViewGroup.triage:
      return <TriageTriangle />;
    case WorkspaceViewGroup.backlog:
      return <BacklogCircle />;
    case WorkspaceViewGroup.epics:
      return <EpicIcon />;
    case WorkspaceViewGroup.planning:
      return <ClockCircleOutlined />;
    case WorkspaceViewGroup.sprint:
      return <SprintIcon />;
    case WorkspaceViewGroup.kanban:
      return <ProjectOutlined />;
    case WorkspaceViewGroup.archive:
      return <ArchiveBox />;
  }
};
